import { motion } from 'framer-motion'
import styled from 'styled-components'
import { BrandLogo } from '@velasca/ui/brand-logo'
import { IconButton } from '@velasca/ui/icon-button'
import { ResponsiveImage } from '@velasca/ui/responsive-image'
import { UnstyledList } from '@velasca/ui/unstyled-list'
import CurrencySelect from '../CurrencySelect'

interface HeaderStatesProps {
  hover: boolean
  isOpen: boolean
  top: boolean
  sticky: boolean
}

const MenuEntry = styled.div`
  transition: color 0.2s cubic-bezier(0, 0, 0, 1) 0s;
  user-select: none;
`

export const FirstLevelMenuTitle = styled(MenuEntry)<{ isSelected: boolean }>`
  display: block;
  text-align: center;
  height: 100%;
  text-transform: uppercase;
  font-weight: ${({ isSelected }) => (isSelected ? '600' : '400')};
  color: ${({ theme, isSelected }) =>
    isSelected ? theme.colors.primary : theme.colors.text.primary};
  letter-spacing: 0.05rem;
  font-size: 1.5rem;
  font-family: ${({ theme }) => theme.fonts.brand};
  padding: 1.3rem 1.8rem;

  &:hover {
    color: ${({ theme }) => theme.colors.primary} !important;
    cursor: pointer;
  }

  &:after {
    display: block;
    content: attr(title);
    font-weight: 600;
    height: 1px;
    color: transparent;
    overflow: hidden;
    visibility: hidden;
  }
`

export const SecondLevelMenuTitle = styled(MenuEntry)`
  text-transform: uppercase;
  font-size: 1.5rem;
  font-family: ${({ theme }) => theme.fonts.brand};
  font-weight: 600;
`

export const ThirdLevelMenuTitle = styled(MenuEntry)`
  font-size: 1.5rem;
  font-family: ${({ theme }) => theme.fonts.brand};
  font-weight: 300;
`

export const Header = styled.div<HeaderStatesProps>`
  z-index: 150;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;

  ${FirstLevelMenuTitle} {
    color: ${({ theme, hover, isOpen, top, sticky }) =>
      top && !isOpen && !hover && !sticky
        ? theme.colors.text.secondary
        : theme.colors.text.primary};
  }
`

export const Background = styled.div`
  position: relative;
`

export const MenuBackground = styled.div`
  position: relative;
`

export const MenuLinksBackground = styled(motion.div)<HeaderStatesProps>`
  position: absolute;
  right: 0;
  left: 0;
  background: ${({ theme, isOpen, hover, top, sticky }) =>
    (isOpen || hover || !top || sticky) && theme.colors.boxBackground};
  overflow: hidden;
`

export const Navbar = styled.nav<HeaderStatesProps>`
  background: ${({ theme, isOpen, hover, top, sticky }) =>
    isOpen || hover || !top || sticky
      ? theme.colors.boxBackground
      : 'transparent'};
  transition: background 0.1s linear;
  line-height: 1.42857143;
`

export const NavHead = styled.div`
  display: flex;
  max-width: 1600px;
  margin: 0 auto;
  padding: 0.5rem 0;
`

export const StyledBrandLogoExtended = styled(BrandLogo.Extended)`
  margin-top: 1rem;
  width: 16rem;
  height: auto;
  transition: opacity 0.1s cubic-bezier(0, 0, 0, 1) 0s;
`

export const StyledBrandLogoShort = styled(BrandLogo.Short)<{
  $alwaysShow: boolean
}>`
  opacity: ${({ $alwaysShow }) => ($alwaysShow ? 1 : 0)};
  transition: opacity 0.1s cubic-bezier(0, 0, 0, 1) 0s;
  width: 4.75rem;
  height: auto;
`

export const Separator = styled.div`
  width: 0.5px;
  margin: 0.75rem;
  transition: color 0.2s cubic-bezier(0, 0, 0, 1) 0s;
`

export const Link = styled.a`
  &:hover,
  &:link,
  &:visited,
  &:active {
    text-decoration: none;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`

export const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
`

export const NavLink = styled(Link)`
  display: block;
  font-size: inherit;
  line-height: inherit;
  color: ${({ theme }) => theme.colors.text.primary};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  ${FirstLevelMenuTitle} {
    &:hover {
      color: ${({ theme }) => theme.colors.primary};
      font-weight: 600;
    }
  }
`

export const HelpLink = styled(Link)`
  font-family: ${({ theme }) => theme.fonts.brand};
  font-weight: 300;
  font-style: italic;
  font-size: 1.4rem;
  transition: color 0.2s cubic-bezier(0, 0, 0, 1) 0s;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    text-decoration: underline;
    cursor: pointer;
  }
`

export const Select = styled(CurrencySelect)`
  font-family: ${({ theme }) => theme.fonts.brand};
  font-size: 1.4rem !important;
  font-weight: 300;
  width: auto;
  border: none;
  background: none !important;
  padding: 0 1.75rem 0 0.75rem;
  margin-bottom: 0;
  line-height: 2rem;
  font-style: italic;
  transition: color 0.2s cubic-bezier(0, 0, 0, 1) 0s;
`

export const HelpMenu = styled.div<HeaderStatesProps>`
  background: ${({ theme, isOpen, hover, top, sticky }) =>
    isOpen || hover || !top || sticky
      ? theme.colors.boxBackground
      : `transparent`};
  transition: background 0.1s linear;
  ${HelpLink} {
    color: ${({ isOpen, theme, hover, sticky, top }) =>
      isOpen || hover || sticky || !top
        ? theme.colors.text.primary
        : theme.colors.text.secondary};
  }

  ${Select} {
    color: ${({ isOpen, theme, hover, sticky, top }) =>
      isOpen || hover || sticky || !top
        ? theme.colors.text.primary
        : theme.colors.text.secondary}!important;
  }

  ${Separator} {
    background: ${({ isOpen, theme, hover, sticky, top }) =>
      isOpen || hover || sticky || !top
        ? theme.colors.text.primary
        : theme.colors.text.secondary};
  }
`

export const HelpMenuContent = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr max-content 1fr;
  grid-template-rows: 1fr;
  padding: 0.25rem;
  overflow: hidden;
`

const HelpChunk = styled.div`
  margin: 0.5rem 2rem;
  height: fit-content;
`

export const StartHelpChunk = styled(HelpChunk)`
  display: flex;
  justify-content: flex-start;
`

export const CenterHelpChunk = styled(HelpChunk)``

export const EndHelpChunk = styled(HelpChunk)`
  display: flex;
  justify-content: flex-end;
`

const Chunk = styled.div`
  display: flex;
  align-items: center;
`

export const StartChunk = styled(Chunk)`
  flex: 1;
  padding-right: 3rem;
`

export const CenterChunk = styled(UnstyledList)`
  display: flex;
  align-items: center;
`

export const EndChunk = styled(Chunk)`
  padding-left: 3rem;
  justify-content: flex-end;
  flex: 1;
`

export const MenuLinks = styled.div`
  display: flex;
  max-width: 1600px;
  margin: 0 auto;
`

export const CenterMenuLinksContainer = styled.div`
  padding: 0 1.8rem;
  display: flex;
  width: 100%;
  gap: 2rem;
  margin: 1rem 0 3rem;
  justify-content: flex-end;
`

const Column = styled.div``

export const MenuColumn = styled(Column)`
  flex: 19%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

export const MediaColumn = styled(Column)`
  flex: 24%;
  max-width: 20rem;
`
export const SecondLevelGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

export const SecondLevelMenuChildren = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

export const ImagesWrapper = styled.div`
  position: relative;
  min-height: 20rem;
`

export const Image = styled(ResponsiveImage)<{ visible: boolean }>`
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  position: absolute;
  top: 0;
  aspect-ratio: 1;
  width: 20rem;
  object-fit: cover;
  display: block;
  border-radius: 2px;
`

export const ImageLink = styled(Link)`
  line-height: 2rem;
`

export const EmptyImage = styled.div<{ visible: boolean }>`
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  position: absolute;
  top: 0;
  aspect-ratio: 1;
  width: 20rem;
  background: ${({ theme }) => theme.colors.boxBackground};
  border-radius: 2px;
`

export const ImageTitle = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.fonts.brand};
  font-size: 1.6rem;
  font-weight: 400;
  font-style: italic;
  min-height: 4rem;
  line-height: 3.4rem;
  display: block;
`

export const CurrencySelector = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const IconWrapper = styled.div`
  position: absolute;
  right: 0;
  pointer-events: none;
`

export const AccessibilityButton = styled(IconButton)`
  user-select: none;
`
